import { XMarkIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { SIDENAV_MENU_ITEMS } from '../../constants';
import MobileMenu from './MobileMenu';
import { useContext, useEffect, useMemo, useState } from 'react';
import { motion, useAnimationControls } from 'framer-motion';
import { MobileNavigationContext, useAuth } from '../../contexts';
import { SystemRoles, ClientCode } from '../../enums/common';
import { ISideNavItem } from '../../types';

import denLogo from '../../assets/images/den_logo_image.svg';
import hathLogo from '../../assets/images/hathway_logo.png';
import softgelLogo from '../../assets/images/softgel_logo.png';
import sapphireLogo from '../../assets/images/sapphire_logo.png';

const containerVariants = {
  close: {
    x: '100%',
    transition: {
      type: 'spring',
      damping: 25,
      stiffness: 300,
    },
  },
  open: {
    x: '0%',
    transition: {
      type: 'spring',
      damping: 25,
      stiffness: 300,
    },
  },
};

const DESKTOP_BREAKPOINT_PX = 768;

const MobileSidebar: React.FC = () => {
  const { isOpen, setIsOpen } = useContext(MobileNavigationContext);
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const containerControls = useAnimationControls();

  const assignedRoles = useMemo(() => {
    if (!user?.userPanelRoles?.length) return [];
    return user.userPanelRoles.map(
      item => item?.role?.name?.toUpperCase() as SystemRoles,
    );
  }, [user?.userPanelRoles]);

  const logoSrc = useMemo(() => {
    const clientCode = user?.companyGroup?.clientCode;
    if (clientCode === ClientCode.DEN) return denLogo;
    if (clientCode === ClientCode.HATHWAY) return hathLogo;
    if (clientCode === ClientCode.SOFTGEL) return softgelLogo;
    if (clientCode === ClientCode.SAPPHIRE || clientCode === 'SAPH') return sapphireLogo;
    return hathLogo;
  }, [user?.companyGroup?.clientCode]);

  const menu = useMemo<ISideNavItem[]>(() => {
    if (assignedRoles.length === 0) return SIDENAV_MENU_ITEMS;

    return SIDENAV_MENU_ITEMS.reduce<ISideNavItem[]>((acc, menuItem) => {
      const allowedRoles = menuItem.allowedRoles || [];
      const hasAccess = assignedRoles.some(role =>
        allowedRoles.includes(role),
      );

      if (!hasAccess) return acc;

      if (menuItem.subMenuItems?.length) {
        const filteredSubItems = menuItem.subMenuItems.filter(subItem =>
          assignedRoles.some(role => subItem.allowedRoles?.includes(role)),
        );
        if (filteredSubItems.length > 0) {
          acc.push({ ...menuItem, subMenuItems: filteredSubItems });
        }
      } else {
        acc.push(menuItem);
      }

      return acc;
    }, []);
  }, [assignedRoles]);

  const filteredMenu = useMemo<ISideNavItem[]>(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return menu;

    return menu.reduce<ISideNavItem[]>((acc, item) => {
      const parentTitle = item.title?.toLowerCase() || '';
      const parentMatch = parentTitle.includes(query);

      if (item.subMenuItems?.length) {
        const filteredSubItems = item.subMenuItems.filter(subItem =>
          (subItem.title || '').toLowerCase().includes(query),
        );

        if (parentMatch || filteredSubItems.length > 0) {
          acc.push({
            ...item,
            subMenuItems: parentMatch ? item.subMenuItems : filteredSubItems,
          });
        }
      } else if (parentMatch) {
        acc.push(item);
      }

      return acc;
    }, []);
  }, [menu, searchTerm]);

  useEffect(() => {
    if (isOpen) {
      containerControls.start('open');
    } else {
      containerControls.start('close');
      setSearchTerm('');
    }
  }, [isOpen, containerControls]);

  // When viewport is widened to desktop (md), close mobile sidebar so it doesn't stay open
  useEffect(() => {
    const media = window.matchMedia(`(min-width: ${DESKTOP_BREAKPOINT_PX}px)`);
    const handler = () => {
      if (media.matches) setIsOpen(false);
    };
    media.addEventListener('change', handler);
    return () => media.removeEventListener('change', handler);
  }, [setIsOpen]);

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/60 z-40 md:hidden print:hidden transition-opacity"
          onClick={() => setIsOpen(false)}
        />
      )}
      <motion.aside
        className="fixed z-50 right-0 top-0 bottom-0 flex flex-col bg-slate-900 text-slate-100 h-screen shadow-2xl w-[260px] max-w-[85vw] md:hidden print:hidden min-h-0 border-l border-slate-800"
        animate={containerControls}
        variants={containerVariants}
        initial="close"
      >
        {/* Header Logo & Close Row */}
        <div className="shrink-0 h-14 px-3.5 flex items-center justify-between border-b border-slate-800 bg-slate-900">
          <div className="flex items-center gap-2">
            {logoSrc ? (
              <img
                src={logoSrc}
                alt="Company Logo"
                className="h-7 w-auto max-w-[120px] object-contain"
              />
            ) : (
              <span className="text-sm font-bold text-slate-100 tracking-wide">
                Navigation
              </span>
            )}
          </div>
          <button
            type="button"
            className="p-1 rounded-md text-slate-400 hover:text-white hover:bg-slate-800 outline-none transition-colors"
            onClick={() => setIsOpen(false)}
            aria-label="Close navigation menu"
          >
            <XMarkIcon className="size-5 cursor-pointer" />
          </button>
        </div>

        {/* Search Menu Input */}
        <div className="shrink-0 px-3 py-2.5 border-b border-slate-800/80 bg-slate-900">
          <div className="relative flex items-center">
            <input
              type="text"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder="Search menu..."
              className="w-full rounded-lg border border-slate-700/80 bg-slate-800/90 px-3 py-1.5 pl-8 text-xs text-slate-100 placeholder:text-slate-400 outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-all"
            />
            <MagnifyingGlassIcon className="absolute left-2.5 size-3.5 text-slate-400 pointer-events-none" />
          </div>
        </div>

        {/* Navigation Items List */}
        <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden scrollbar-hide py-2">
          <ul className="flex flex-col gap-0.5 px-2">
            {filteredMenu.map((item, idx) => (
              <li key={item.title || idx}>
                <MobileMenu item={item} searchTerm={searchTerm} />
              </li>
            ))}
            {filteredMenu.length === 0 && (
              <li className="px-3 py-4 text-xs text-slate-400 text-center">
                No matching menu found
              </li>
            )}
          </ul>
        </div>
      </motion.aside>
    </>
  );
};

export default MobileSidebar;
